import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const WorkflowManagementModule = () => {
  const [activeSection, setActiveSection] = useState("workflow-designer");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleWorkflow = () => navigate('/access-recruit');

  const sectionContent = {
    "workflow-designer": {
      title: "Visual Workflow Designer",
      description: "Create and customize complex HR workflows with our intuitive drag-and-drop workflow designer. Build approval chains, automation sequences, and process flows without coding. Configure conditional logic, parallel processing, and exception handling. Design workflows for onboarding, offboarding, promotions, transfers, and policy changes. Preview workflow execution, test scenarios, and deploy changes with version control and rollback capabilities."
    },
    "approval-management": {
      title: "Intelligent Approval",
      description: "Streamline approval processes with smart routing, delegation, and escalation rules. Configure multi-level approval hierarchies based on organizational structure, cost thresholds, and policy requirements. Implement automatic delegation during absences and parallel approvals for faster processing. Track approval status in real-time, send automated reminders, and provide approvers with context-rich information for informed decision-making across all HR processes."
    },
    "process-automation": {
      title: "End-to-End Process",
      description: "Automate complete HR process journeys from initiation to completion with intelligent workflow automation. Connect systems, trigger actions, and execute tasks based on predefined conditions. Automate data updates, document generation, notifications, and system integrations. Monitor process execution, identify bottlenecks, and optimize workflows based on performance analytics. Ensure consistency and compliance while reducing manual intervention in routine HR operations."
    },
    "task-management": {
      title: "Automated Task Management",
      description: "Coordinate and track tasks across HR processes with automated assignment and monitoring systems. Assign tasks to individuals, teams, or roles based on workflow progress. Set deadlines, priorities, and dependencies between tasks. Send automated reminders for overdue tasks and escalate unresolved items. Provide dashboards for task status tracking and performance monitoring. Integrate task completion with process progression and trigger subsequent workflow steps automatically."
    },
    "workflow-analytics": {
      title: "Workflow Analytics",
      description: "Gain insights into workflow performance with comprehensive analytics and optimization tools. Track key metrics including cycle times, approval rates, bottleneck identification, and process efficiency. Analyze workflow patterns, identify improvement opportunities, and simulate optimization scenarios. Measure workflow impact on HR service delivery, employee satisfaction, and operational costs. Use data-driven insights to continuously refine and optimize HR workflows for maximum efficiency."
    }
  };

  const sections = [
    { id: "workflow-designer", label: "DESIGNER" },
    { id: "approval-management", label: "APPROVALS" },
    { id: "process-automation", label: "AUTOMATION" },
    { id: "task-management", label: "TASKS" },
    { id: "workflow-analytics", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Workflow Designer */}
        <section
          ref={(el) => (sectionsRef.current["workflow-designer"] = el)}
          data-section="workflow-designer"
          className={`resto-section ${visibleSections.has("workflow-designer") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Design Tools</div>
            <h2 className="resto-section-title">
              {sectionContent["workflow-designer"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["workflow-designer"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleWorkflow}>Design Workflow</button>
              <button className="resto-secondary-btn" onClick={handleWorkflow}>Browse Templates</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/wow1.png" className="resto-real-image" alt="Workflow Designer" />
          </div>
        </section>

        {/* 2. Approval Management */}
        <section
          ref={(el) => (sectionsRef.current["approval-management"] = el)}
          data-section="approval-management"
          className={`resto-section resto-reverse ${visibleSections.has("approval-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Approvals</div>
            <h2 className="resto-section-title">
              {sectionContent["approval-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["approval-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleWorkflow}>Configure Approvals</button>
              <button className="resto-secondary-btn" onClick={handleWorkflow}>Track Status</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/wow2.png" className="resto-real-image" alt="Approval Management" />
          </div>
        </section>

        <CTA/>

        {/* 3. Process Automation */}
        <section
          ref={(el) => (sectionsRef.current["process-automation"] = el)}
          data-section="process-automation"
          className={`resto-section ${visibleSections.has("process-automation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Automation</div>
            <h2 className="resto-section-title">
              {sectionContent["process-automation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["process-automation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleWorkflow}>Automate Process</button>
              <button className="resto-secondary-btn" onClick={handleWorkflow}>Monitor Execution</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/wow3.png" className="resto-real-image" alt="Process Automation" />
          </div>
        </section>

        {/* 4. Task Management */}
        <section
          ref={(el) => (sectionsRef.current["task-management"] = el)}
          data-section="task-management"
          className={`resto-section resto-reverse ${visibleSections.has("task-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Tasks</div>
            <h2 className="resto-section-title">
              {sectionContent["task-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["task-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleWorkflow}>Assign Tasks</button>
              <button className="resto-secondary-btn" onClick={handleWorkflow}>Track Progress</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/wow4.png" className="resto-real-image" alt="Task Management" />
          </div>
        </section>

        {/* 5. Workflow Analytics */}
        <section
          ref={(el) => (sectionsRef.current["workflow-analytics"] = el)}
          data-section="workflow-analytics"
          className={`resto-section ${visibleSections.has("workflow-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["workflow-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["workflow-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleWorkflow}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleWorkflow}>Optimize Workflows</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/wow5.png" className="resto-real-image" alt="Workflow Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default WorkflowManagementModule;